import React, { useEffect, useState } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import StatusBadge from '../components/ui/StatusBadge';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { PLATFORM_API_URL } from '../services/platformApi';

function webhookHint(code) {
  return `${PLATFORM_API_URL.replace(/\/$/, '')}/api/v1/billing/payments/webhook?provider=${code || 'razorpay'}`;
}

function PaymentProviders() {
  const toast = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    status: 'disabled',
    mode: 'sandbox',
    priority: 10,
    keyId: '',
    keySecret: '',
    webhookSecret: '',
  });

  useEffect(() => {
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/payment-providers/list', {});
      setProviders(response.data?.data?.providers || []);
    } catch (error) {
      console.error('Error fetching payment providers:', error);
      toast.error(error.response?.data?.message || 'Failed to load payment providers');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (provider) => {
    setEditing(provider);
    setForm({
      status: provider.status || 'disabled',
      mode: provider.mode || 'sandbox',
      priority: provider.priority ?? 10,
      keyId: '',
      keySecret: '',
      webhookSecret: '',
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'priority' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const goingLive = form.mode === 'production' && editing.mode !== 'production';
    if (goingLive) {
      const ok = await confirm({
        title: `Switch ${editing.name} to production?`,
        message: 'Customers will be charged real money from the next checkout. Make sure live keys and the live webhook secret are saved, the webhook (payment, refund and dispute events) points to this platform, and seller GST details are filled in.',
        confirmLabel: 'Go live',
      });
      if (!ok) return;
    }
    if (form.status === 'disabled' && editing.status === 'active') {
      const ok = await confirm({
        title: `Disable ${editing.name}?`,
        message: 'Tenants will not be able to pay until another provider is active.',
        confirmLabel: 'Disable',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = {
        code: editing.code,
        status: form.status,
        mode: form.mode,
        priority: form.priority,
      };
      // Only send credential fields when the user typed new values (leave blank = keep existing).
      if (form.keyId.trim()) payload.keyId = form.keyId.trim();
      if (form.keySecret.trim()) payload.keySecret = form.keySecret.trim();
      if (form.webhookSecret.trim()) payload.webhookSecret = form.webhookSecret.trim();

      await api.post('/admin/payment-providers/update', payload);
      toast.success(`${editing.name} updated`);
      setShowModal(false);
      await fetchProviders();
    } catch (error) {
      console.error('Error updating payment provider:', error);
      toast.error(error.response?.data?.message || 'Failed to update payment provider');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading payment providers..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Payment Providers"
        description="Configure Razorpay and future gateways. Secrets are encrypted at rest and never shown in full."
      />

      <div className="card card__body" style={{ marginBottom: '1rem' }}>
        <p className="form-hint" style={{ margin: 0 }}>
          Razorpay webhook URL (register in Razorpay Dashboard → Webhooks):
          {' '}
          <code className="cell-mono">{webhookHint('razorpay')}</code>
        </p>
      </div>

      {providers.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No payment providers"
            description="Provider catalog has not been seeded yet. Restart the auth service or retry."
            action={
              <button type="button" className="btn btn--primary" onClick={fetchProviders}>
                Refresh
              </button>
            }
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Key ID</th>
                  <th>Secrets</th>
                  <th>Implemented</th>
                  <th>Priority</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => (
                  <tr key={provider.code}>
                    <td data-label="Provider">
                      <strong>{provider.name}</strong>
                      <div className="form-hint cell-mono">{provider.code}</div>
                    </td>
                    <td data-label="Status">
                      <StatusBadge status={provider.status === 'active' ? 'Active' : 'Disabled'} />
                    </td>
                    <td data-label="Mode">{provider.mode || '—'}</td>
                    <td data-label="Key ID" className="cell-mono">
                      {provider.credentials?.keyId || (provider.credentials?.keyIdConfigured ? 'Configured' : '—')}
                    </td>
                    <td data-label="Secrets">
                      {[
                        provider.credentials?.keySecretConfigured ? 'Key' : null,
                        provider.credentials?.webhookSecretConfigured ? 'Webhook' : null,
                      ].filter(Boolean).join(', ') || 'Not set'}
                    </td>
                    <td data-label="Implemented">
                      {provider.implemented ? 'Yes' : 'Future'}
                    </td>
                    <td data-label="Priority">{provider.priority}</td>
                    <td data-label="Actions" className="cell-actions">
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        onClick={() => openEdit(provider)}
                      >
                        Configure
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && editing && (
        <Modal
          title={`Configure ${editing.name}`}
          subtitle={editing.implemented ? editing.code : `${editing.code} — not implemented yet`}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            {!editing.implemented && (
              <div className="card card__body" style={{ marginBottom: '1rem', background: 'var(--surface-2, #f8fafc)' }}>
                <p className="form-hint" style={{ margin: 0 }}>
                  This provider can be saved as disabled for future use. Activation requires a backend implementation.
                </p>
              </div>
            )}

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label htmlFor="status" className="form-label">Status</label>
                <select
                  id="status"
                  name="status"
                  className="form-select"
                  value={editing.implemented ? form.status : 'disabled'}
                  onChange={handleFormChange}
                  disabled={!editing.implemented}
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="mode" className="form-label">Mode</label>
                <select
                  id="mode"
                  name="mode"
                  className="form-select"
                  value={form.mode}
                  onChange={handleFormChange}
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="priority" className="form-label">Priority</label>
              <input
                type="number"
                id="priority"
                name="priority"
                className="form-input"
                min={1}
                max={1000}
                value={form.priority}
                onChange={handleFormChange}
              />
              <span className="form-hint">Lower number = higher priority when resolving the active provider.</span>
            </div>

            <div className="form-group">
              <label htmlFor="keyId" className="form-label">Key ID</label>
              <input
                type="text"
                id="keyId"
                name="keyId"
                className="form-input"
                value={form.keyId}
                onChange={handleFormChange}
                placeholder={
                  editing.credentials?.keyId
                    || (editing.credentials?.keyIdConfigured ? 'Leave blank to keep current' : 'rzp_test_...')
                }
                autoComplete="off"
              />
              {editing.credentials?.keyId && (
                <span className="form-hint">Current masked: {editing.credentials.keyId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="keySecret" className="form-label">Key Secret</label>
              <input
                type="password"
                id="keySecret"
                name="keySecret"
                className="form-input"
                value={form.keySecret}
                onChange={handleFormChange}
                placeholder={
                  editing.credentials?.keySecretConfigured
                    ? 'Leave blank to keep encrypted secret'
                    : 'Enter key secret'
                }
                autoComplete="new-password"
              />
              <span className="form-hint">
                {editing.credentials?.keySecretConfigured ? 'Stored as Encrypted' : 'Not configured'}
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="webhookSecret" className="form-label">Webhook Secret</label>
              <input
                type="password"
                id="webhookSecret"
                name="webhookSecret"
                className="form-input"
                value={form.webhookSecret}
                onChange={handleFormChange}
                placeholder={
                  editing.credentials?.webhookSecretConfigured
                    ? 'Leave blank to keep encrypted secret'
                    : 'From Razorpay Dashboard → Webhooks'
                }
                autoComplete="new-password"
              />
              <span className="form-hint">
                Webhook URL: <code className="cell-mono">{webhookHint(editing.code)}</code>
              </span>
            </div>

            <div className="btn-group" style={{ justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default PaymentProviders;
